import { MouseEventHandler, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { FormInput } from "../components/custom/components";
import DeleteConfirmSales from "../components/DeleteConfirmSales";
import Modal from "../components/Modal";
import { ToastTypes, useToast } from "../components/toast/ToastProvider";
import { apiUrl, FormType, FormTypeList } from "../config";
import { decodeHtml, formatBillTimeFromCreatedAt, formatInrTwoDecimals, getDate, LocalFormatDate, rupeeFormat } from "../config/functions";
import { AuthUserType, RoleType } from "../redux/actions/authUserActions";
import { ClientRegistrationType } from "../redux/actions/clientRegistration";
import { CustomerType, fetchCustomerAction } from "../redux/actions/customerAction";
import { ProductType } from "../redux/actions/productAction";
import {
    fetchSalesByInvocieDateAction,
    SalesModeDropDown,
    SalesPaymentModesDropDown,
    SalesType,
} from "../redux/actions/salesActions";
import { getSettings } from "../services/appSetting.service";
import { AppDispatch, StoreState } from "../redux/store";
import { getSalesById } from "../services/sales.service ";
import { SalesEditAttributes } from "./SalesMaster";
import { AccountHolderType } from "../redux/actions/accountHolder";
import { getCustomer } from "../services/customer.services";
import { generateSalesBillPdf } from "../utils/salesBillPdf";
import { resolvePlaceOfSupply } from "../utils/placeOfSupplyOptions";
import {
    isThermalPrinterFromSettings,
    postThermalSalesReceiptIfNeeded,
    type SalesPrintObject,
} from "../utils/thermalSalesReceiptPrint";

function thermalPaymentLabel(paymentMode: string | undefined): string {
    return paymentMode === SalesPaymentModesDropDown.CASH
        ? "CASH SALES"
        : paymentMode || "CASH SALES";
}

const formatSalesModeLabel = (mode?: string) =>
    mode === SalesModeDropDown.ONLINE ? "Online" : "Offline";

interface TableBody {
    sno: number;
    invoiceDate: string;
    invoiceNo: string;
    salesEntryNo: string;
    customer: string;
    representer: string;
    salesMode: string;
    finalAmount: string;
    onEdit: MouseEventHandler<HTMLButtonElement>;
    onDelete: MouseEventHandler<HTMLButtonElement>;
    onPrint: MouseEventHandler<HTMLButtonElement>;
    rolesAccessObj: RoleType | undefined
}

type HandleEdit = (value: SalesType) => void;
type HandleDelete = (value: SalesType) => void;
type HandlePrint = (value: SalesType) => void;

interface Props {
    setFormType: React.Dispatch<React.SetStateAction<FormType>>;
    setId: React.Dispatch<React.SetStateAction<number | undefined>>;
    handleEdit?: () => void;
    onEditComplete?: () => void;
    onclose: () => void;
    /** When the view modal opens, refetch sales for the selected date */
    viewOpen?: boolean;
}

const toApiInvoiceDate = (date: Date | null): string =>
    getDate(date ?? new Date());

const Sales = (props: Props) => {
    const dispatch = useDispatch<AppDispatch>();
    const toast = useToast();
    const salesList = useSelector<StoreState, SalesType[]>((state) => state.sales);
    const AccountHoldersList = useSelector<StoreState, AccountHolderType[]>((state) => state.account);
    const [customerList, setcustomerList] = useState<CustomerType[]>([]);
    const [showDeleteForm, setShowDeleteForm] = useState<boolean>(false);
    const [searchDate, setSearchDate] = useState<Date | null>(new Date());
    const [searchCustomer, setSearchCustomer] = useState<string>("");
    const [searchInvoiceNo, setSearchInvoiceNo] = useState<string>("");
    const [editData, setEditData] = useState<SalesType>();

    const clients = useSelector<StoreState, ClientRegistrationType[]>((state) => state.clientRegistration);
    const AuthUser = useSelector<StoreState, AuthUserType | null>((state) => state.authUser);
    const ProductList = useSelector<StoreState, ProductType[]>((state) => state.product);

    const url = window.location.pathname
    const rolesAccessObj = AuthUser?.roles?.find(f => f.pageUrl === url)

    const fetchCustomer = async () => {
        const res = await getCustomer();
        setcustomerList(res.data.data);
    };

    const loadSalesForDate = (date: Date | null) => {
        dispatch(fetchSalesByInvocieDateAction(toApiInvoiceDate(date)));
    };

    useEffect(() => {
        if (props.viewOpen !== false) {
            loadSalesForDate(searchDate);
            fetchCustomer();
        }
    }, [props.viewOpen, dispatch]);

    const handleDateFilter = (date: Date | null) => {
        setSearchDate(date);
        loadSalesForDate(date);
    };
    const handleCustomerFilter = (value: string) => setSearchCustomer(value.toLowerCase());
    const handleInvoiceNoFilter = (value: string) => setSearchInvoiceNo(value.toLowerCase());

    // ✅ Compare only date parts (ignore time)

    const handleEdit: HandleEdit = (value) => {
        props.setFormType(FormTypeList.EDIT);
        props.setId(value.id);
        setEditData(value);
        props.onclose();
    };

    const handleDelete: HandleDelete = (value) => {
        props.setFormType(FormTypeList.DELETE);
        setEditData(value);
        setShowDeleteForm(true);
    };

    // ✅ FIX 2: Print flow with proper PDF generation
    const handlePrintPdf: HandlePrint = async (value) => {
        if (!value?.id) {
            toast("Invalid sales data", ToastTypes.ERROR);
            return;
        }

        try {
            const response = await getSalesById(value.id);
            const data: SalesEditAttributes = response.data.data;

            const productMap = new Map(
                ProductList?.map((product) => [product.productCode?.toString(), product])
            );

            const cust = customerList?.find(
                (c) => c.accountNumber?.toString() === data.customer_id?.toString()
            );
            const productitems = data.salesItems
                ?.filter((item: any) => item.product_code)
                ?.map((item: any) => {                    
                    const product = productMap.get(item.product_code?.toString());
                    const modelHint = String(item.model_number ?? "").trim();
                    const apiName = String(item.product_name ?? "").trim();
                    const catalogName =
                        apiName ||
                        (product?.productName &&
                        String(product.productName).trim() !== modelHint
                            ? product.productName
                            : "") ||
                        item.product_code;
                    const hsnVal = item.sac ?? product?.sac;
                    return {
                        name: catalogName,
                        pcode: item.model_number,
                        hsn:
                            hsnVal != null && String(hsnVal) !== ""
                                ? String(hsnVal)
                                : "-",
                        qty: item.quantity,
                        rate: parseFloat(item.w_rate || "0").toFixed(2),
                        gst: product?.gst || "0",
                        total: parseFloat(item.total_amount || "0").toFixed(2),
                    };
                });

            if (!productitems?.length) {
                toast("No items to print", ToastTypes.ERROR);
                return;
            }

            const billDate = LocalFormatDate(data.invoice_date);
            const billTime = formatBillTimeFromCreatedAt(data.createdAt);

            const printObj = {
                kotnumber: 1,
                invoiceType: data.invoice_type,
                taxable: data.net_taxable,
                cgst: data.net_cgst,
                sgst: data.net_sgst,
                previousBal: data.prev_balance,
                credit: data.credit,
                billnumber: data.invoice_number?.toString(),
                tablename: "table1",
                customer: cust,
                cashierid: AuthUser?.userId,
                systemid: data.system_no,
                placeOfSupply: resolvePlaceOfSupply(data.place_of_supply),
                date: billDate,
                printername: "",
                time: billTime,
                productitems,
                empname: AuthUser?.name,
                grandtotal: data.grand_total,
                discount: [],
                gst: [],
                oc: [],
                finaltotal: parseFloat(data.grand_total || "0").toFixed(2),
                totaldisc: parseFloat(data.dis_amount || "0").toFixed(2),
                totalqty: data.salesItems?.reduce(
                    (sum: number, si: any) => sum + Number(si.total_quantity || 0),
                    0
                ),
                paymentamount: (() => {
                    const isCredit =
                        String(data.payment_mode ?? "").toLowerCase() === "credit";
                    if (isCredit) return "0.00";
                    const paid = parseFloat(data.amt_paid || "0");
                    const gt = parseFloat(data.grand_total || "0");
                    return (paid > 0 ? paid : gt).toFixed(2);
                })(),
                paymentMode: data.payment_mode,
                tender: data.tender_amount ? parseFloat(data.tender_amount.toString()).toFixed(2) : "0",
                balance: data.tender_return ? parseFloat(data.tender_return.toString()).toFixed(2) : "0",
            };

            const thermalPrintObj: SalesPrintObject = {
                billnumber: printObj.billnumber || "",
                date: billDate,
                time: billTime,
                customer: {
                    name: decodeHtml(cust?.accountName || ""),
                    place: cust?.place || "",
                    conNo: cust?.contactNumber1 != null ? String(cust.contactNumber1) : "",
                    conNo2: cust?.contactNumber2 != null ? String(cust.contactNumber2) : "",
                    gstNo: cust?.gstNo || "NIL",
                },
                productitems,
                finaltotal: printObj.finaltotal,
                paymentamount: printObj.paymentamount,
                credit: String(printObj.credit ?? "0"),
                previousBal: String(printObj.previousBal ?? "0"),
            };

            const branchId = Number(AuthUser?.branchId) || 0;
            let settings;
            try {
                settings = await getSettings();
            } catch {
                settings = [];
            }

            if (isThermalPrinterFromSettings(settings, branchId)) {
                const r = await postThermalSalesReceiptIfNeeded({
                    printObj: thermalPrintObj,
                    client: clients[0],
                    settings,
                    branchId,
                    paymentLabel: thermalPaymentLabel(data.payment_mode),
                    apiBaseUrl: apiUrl || "",
                });
                if (r.attempted && !r.ok) {
                    toast(r.error || "Thermal printer request failed", ToastTypes.ERROR);
                }
                return;
            }

            const pdfBlob = await generateSalesBillPdf(
                printObj as Parameters<typeof generateSalesBillPdf>[0],
                clients[0],
                {
                settings,
                branchId,
                paymentLabel: thermalPaymentLabel(data.payment_mode),
                apiBaseUrl: apiUrl || "",
                },
            );
            const pdfUrl = URL.createObjectURL(pdfBlob);
            const printWindow = window.open(pdfUrl, "_blank");

            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            } else {
                // Fallback: direct download if popup blocked
                const a = document.createElement("a");
                a.href = pdfUrl;
                a.download = `Invoice-${printObj.billnumber}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                toast("PDF downloaded. Please allow popups for print preview.");
            }

            setTimeout(() => URL.revokeObjectURL(pdfUrl), 3000);
        } catch (error) {
            console.error("Print error:", error);
            toast("Failed to generate invoice", ToastTypes.ERROR);
        }
    };

    const handleDeleteFormClose = () => {
        setShowDeleteForm(false);
        props.setFormType(FormTypeList.ADD);
        // ✅ Refresh sales list after delete
        loadSalesForDate(searchDate);
    };

    // ✅ FIX 1: Proper date filtering with isSameDate helper
    const filteredSales = salesList?.filter((tr) => {
        const invoiceNoMatch = searchInvoiceNo
            ? tr.invoiceNumber?.toLowerCase().includes(searchInvoiceNo)
            : true;

        const customerObj = customerList.find(
            (s) => s?.accountNumber?.toString() === tr?.customerId?.toString()
        );
        const customerName = customerObj?.accountName?.toLowerCase() || "";
        const customerMatch = searchCustomer
            ? customerName.includes(searchCustomer)
            : true;

        return invoiceNoMatch && customerMatch;
    });

    return (
        <>
            <div className="row mb-3 align-items-center">
                <div className="col-lg-8">
                    <div className="hstack gap-4">
                        <div>
                            <label htmlFor="searchDate">Date :</label>
                            <DatePicker
                                id="searchDate"
                                selected={searchDate}
                                onChange={handleDateFilter}
                                dateFormat="dd-MM-yyyy"
                                className="form-control form-control-sm"
                            />
                        </div>
                        <FormInput
                            name="searchInvoiceNo"
                            label="Invoice No :"
                            value={searchInvoiceNo}
                            onChange={(e) => handleInvoiceNoFilter(e.target.value)}
                            containerClass="mb-0"
                        />
                        <FormInput
                            name="searchCustomer"
                            label="Customer :"
                            value={searchCustomer}
                            onChange={(e) => handleCustomerFilter(e.target.value)}
                            containerClass="mb-0"
                        />
                    </div>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-6">
                    <p className="text-center fs-3 fw-bold m-0">{"Total Amount : "} <span className="text-primary">{"₹"+formatInrTwoDecimals(filteredSales.reduce((acc, i) => acc + Number(i.grandTotal), 0))}</span></p>
                </div>
            </div>

            <Modal headerText={"Delete Sales"} visible={showDeleteForm} onClose={handleDeleteFormClose} centered size="sm">
                <DeleteConfirmSales
                    editData={editData}
                    onClose={() => {
                        handleDeleteFormClose();
                        // ✅ FIX 1: Refresh after delete
                        loadSalesForDate(searchDate);
                    }}
                />
            </Modal>

            <div className="row">
                <div className="col-lg-12">
                    {/* <div className="row">
                        <div className="col-lg-5 col-md-0 col-sm-0"></div>
                        <div className="col-lg-5 col-md-6 col-sm-6"></div>
                        <div className="col-lg-2 col-md-6 col-sm-6 display-flex gap-0">
                            <p className="text-center fs-3 fw-bold">Total Amount</p>
                            <p className="text-center fs-3 fw-bold">
                                {"₹"+filteredSales.reduce((acc, i) => acc + Number(i.grandTotal), 0)}
                            </p>
                        </div>
                    </div> */}
                    <div className="card shadow-lg">
                        <div className="table-wrapper">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th className="text-truncate align-middle">S.No</th>
                                        <th className="text-truncate align-middle">Sales Entry No</th>
                                        <th className="text-truncate align-middle">Bill Date</th>
                                        <th className="text-truncate align-middle">Bill No</th>
                                        <th className="text-truncate align-middle">Customer</th>
                                        <th className="text-truncate align-middle">Representer</th>
                                        <th className="text-truncate align-middle">Sales Mode</th>
                                        <th className="text-truncate align-middle">Grand Total</th>
                                        <th className="text-truncate align-middle">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSales?.map((tr, i) => {
                                        const customerObj = customerList?.find((s) => s?.accountNumber?.toString() === tr?.customerId?.toString());
                                        const representerObj = AccountHoldersList?.find(
                                            (s) => {
                                                 return s?.accountNumber?.toString() === tr?.repId?.toString()}
                                        );

                                        const customerName = decodeHtml(customerObj?.accountName || "");
                                        const repName = decodeHtml(representerObj?.accountName || "N/A");

                                        return (
                                            <UserBody
                                                sno={i + 1}
                                                invoiceNo={tr.invoiceNumber?.toString()}
                                                invoiceDate={tr.invoiceDate?.toString()}
                                                salesEntryNo={tr.salesEntryNumber?.toString()}
                                                finalAmount={tr.grandTotal?.toString() || "0"}
                                                customer={customerName}
                                                representer={repName}
                                                salesMode={formatSalesModeLabel(tr.salesMode)}
                                                onEdit={() => handleEdit(tr)}
                                                onDelete={() => handleDelete(tr)}
                                                onPrint={() => handlePrintPdf(tr)}
                                                rolesAccessObj={rolesAccessObj}
                                                key={tr.id || i}
                                            />
                                        );
                                    })}
                                    {filteredSales?.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="text-center py-4">No records found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const UserBody = ({ sno, invoiceDate, finalAmount, invoiceNo, salesEntryNo, customer, representer, salesMode, onEdit, onDelete, onPrint, rolesAccessObj }: TableBody) => {
    return (
        <tr key={sno}>
            <td className="align-middle">{sno}</td>
            <td className="text-capitalize align-middle">{salesEntryNo}</td>
            <td className="text-capitalize align-middle">{LocalFormatDate(invoiceDate)}</td>
            <td className="text-capitalize align-middle">{invoiceNo}</td>
            <td className="text-capitalize align-middle">{customer}</td>
            <td className="text-capitalize align-middle">{representer}</td>
            <td className={`text-capitalize align-middle ${salesMode === 'Online' ? 'text-success' : 'text-primary'}`}>{salesMode}</td>
            <td className="text-capitalize align-middle">{rupeeFormat(finalAmount)}</td>
            <td className="align-middle">
                <div className="hstack gap-1">
                    {(rolesAccessObj?.edit || rolesAccessObj?.delete) &&
                        <div className="hstack gap-1">
                            {rolesAccessObj?.edit && <button className="btn btn-sm fw-bold btn-info p-1" onClick={onEdit}>
                                <i className="fe-edit noti-icon"></i>
                            </button>}
                            {rolesAccessObj?.delete && <button className="btn btn-sm fw-bold btn-danger p-1" onClick={onDelete}>
                                <i className="fe-trash-2 noti-icon"></i>
                            </button>}
                        </div>
                    }

                    <button className="btn btn-sm fw-bold btn-success p-1" onClick={onPrint}>
                        <i className="fe-printer noti-icon"></i>
                    </button>
                </div>
            </td>
        </tr>
    );
};

export default Sales;